'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { NavBar } from '@/components/Navbar';
import { SidebarProvider } from '@/components/ui/sidebar';
import Header from '@/components/header';
import Loading from '@/components/loading';
import RouteProtection from '@/components/RouteProtection';
import { getUserRole, ROLES, isAuthenticated } from '@/lib/auth';
import { useSyncCurrentUserRole } from '@/hooks/useUsers';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';

export default function ClientLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  // Note: do not use `includes('/service')` alone — it matches `/services` and misroutes the stores list.
  const isAuth =
    pathname?.startsWith('/login') ||
    pathname?.startsWith('/register') ||
    pathname?.startsWith('/verify-otp') ||
    (pathname?.includes('/service') && !pathname?.startsWith('/services'));

  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userRole, setUserRole] = useState<
    (typeof ROLES)[keyof typeof ROLES] | null
  >(null);
  const [isHydrated, setIsHydrated] = useState(false);

  // Sync current user role from backend (only when authenticated and not on auth pages)
  const shouldSyncRole = !isAuth && isAuthenticated();
  useSyncCurrentUserRole(shouldSyncRole);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Customer shell routes: no fake route-transition loader (URLs use /home, not the (main) segment).
  const skipRouteTransitionLoading =
    pathname?.startsWith('/main') ||
    pathname === '/home' ||
    pathname?.startsWith('/home/') ||
    pathname === '/services' ||
    pathname?.startsWith('/services/') ||
    pathname === '/store-details' ||
    pathname?.startsWith('/store-details/');

  useEffect(() => {
    if (skipRouteTransitionLoading) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, [pathname, skipRouteTransitionLoading]);

  // Handle hydration and user role
  useEffect(() => {
    setIsHydrated(true);
    const role = getUserRole() || ROLES.USER;
    setUserRole(role);
  }, []);

  // Re-check user role when pathname changes (e.g., after login redirect)
  // This ensures the navbar shows correctly after login
  useEffect(() => {
    if (isHydrated && !isAuth) {
      const role = getUserRole() || ROLES.USER;
      setUserRole(role);
    }
  }, [pathname, isHydrated, isAuth]);

  // Listen for userRole changes in localStorage to immediately update UI
  useEffect(() => {
    if (!isHydrated || isAuth) return;

    const checkUserRole = () => {
      const role = getUserRole() || ROLES.USER;
      setUserRole(role);
    };

    // Listen for storage changes (when localStorage is updated in another tab/component)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userRole') {
        checkUserRole();
      }
    };

    // Listen for custom storage event (when localStorage is updated in same tab)
    const handleCustomStorageChange = () => {
      checkUserRole();
    };

    // Poll localStorage frequently to catch same-tab changes instantly
    const pollInterval = setInterval(() => {
      checkUserRole();
    }, 100); // Check every 100ms for instant updates

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userRoleChanged', handleCustomStorageChange);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userRoleChanged', handleCustomStorageChange);
    };
  }, [isHydrated, isAuth]);

  if (isAuth) {
    return <>{isLoading ? <Loading size={50} /> : children}</>;
  }

  // Show loading during hydration to prevent mismatch
  if (!isHydrated) {
    return (
      <>
        {isLoading ? <Loading size={50} /> : children}
      </>
    );
  }

  // Get user role to determine layout
  const currentUserRole = userRole || ROLES.USER;
  const isUser = currentUserRole === ROLES.USER;

  return (
    <RouteProtection>
      {/* <PWAInstallPrompt /> - Using default browser install prompt */}
      {isUser ? (
        // Full-width layout for regular users (no navigation)
        <div className="w-full h-screen overflow-hidden" suppressHydrationWarning>
          <main className="w-full h-full overflow-x-hidden" suppressHydrationWarning>
            {isLoading && !skipRouteTransitionLoading ? (
              <div className="flex items-center justify-center min-h-[400px]" suppressHydrationWarning>
                <Loading size={60} />
              </div>
            ) : (
              children
            )}
          </main>
        </div>
      ) : (
        // Normal layout with navigation for admin role
        <SidebarProvider>
          <div className="flex w-full overflow-hidden bg-sidebar" suppressHydrationWarning>
            <NavBar />
            <div className="flex flex-1 flex-col overflow-hidden min-w-0">
              {/* Desktop: header with sidebar collapse icon and dark mode toggle - match sidebar bg/border */}
              <div className="hidden md:block shrink-0 border-b border-sidebar-border bg-sidebar transition-colors">
                <Header />
              </div>
              <main
                className={`flex-1 p-4 transition-all duration-300 overflow-x-hidden bg-sidebar ${isMobile ? 'pt-20 pb-24' : ''}`}
                suppressHydrationWarning>
                {isLoading && !skipRouteTransitionLoading ? (
                  <div className="flex items-center justify-center min-h-[400px]" suppressHydrationWarning>
                    <Loading size={60} />
                  </div>
                ) : (
                  children
                )}
              </main>
            </div>
          </div>
        </SidebarProvider>
      )}
    </RouteProtection>
  );
}
